import type {
  Collaborator,
  EngagementComment,
  KpiMetric,
  ContentFormat,
  ContentVariant,
  MarketingContentItem,
  PostIdea,
  PostTemplate,
  SentimentPoint,
  SocialPost,
  TraditionalPlacement,
  TrendMetric,
  WorkFeedback,
} from '@/client/types/marketing';

export const MARKETING_COLLABORATORS: Collaborator[] = [
  {
    id: 'col_ama',
    name: 'Ama Boateng',
    email: 'ama@fairful.com',
    phone: '+233 24 000 1001',
    role: 'author',
    sourceLinks: [
      { sourceId: 'acc_ff_ig', isAccountHolder: true },
      { sourceId: 'acc_ff_fb', isAccountHolder: true },
    ],
    active: true,
    avatarColor: 'bg-amber-500',
    joinedAt: '2025-11-03T00:00:00Z',
    notes: 'Brand social lead · holds Fairful IG & Facebook',
  },
  {
    id: 'col_kwesi',
    name: 'Kwesi Owusu',
    email: 'kwesi@fairful.com',
    role: 'editor',
    sourceLinks: [
      { sourceId: 'acc_ff_x', isAccountHolder: false },
      { sourceId: 'acc_ff_li', isAccountHolder: false },
    ],
    active: true,
    avatarColor: 'bg-sky-500',
    joinedAt: '2025-09-12T00:00:00Z',
    notes: 'Editorial contact for X and LinkedIn; Efua holds the logins',
  },
  {
    id: 'col_efua',
    name: 'Efua Mensah',
    email: 'efua@fairful.com',
    role: 'social_manager',
    sourceLinks: [
      { sourceId: 'acc_ff_x', isAccountHolder: true },
      { sourceId: 'acc_ff_li', isAccountHolder: true },
      { sourceId: 'acc_ff_tt', isAccountHolder: true },
      { sourceId: 'acc_ff_yt', isAccountHolder: true },
    ],
    active: true,
    avatarColor: 'bg-fuchsia-500',
    joinedAt: '2026-01-20T00:00:00Z',
  },
  {
    id: 'col_jordan',
    name: 'Jordan Lee',
    email: 'jordan@fairful.com',
    role: 'reviewer',
    sourceLinks: [{ sourceId: 'out_techcrunch', isAccountHolder: false }],
    active: true,
    avatarColor: 'bg-emerald-500',
    joinedAt: '2025-12-01T00:00:00Z',
    notes: 'PR liaison for TechCrunch pitches — not outlet staff',
  },
  {
    id: 'col_naa',
    name: 'Naa Adjei',
    email: 'naa@fairful.com',
    role: 'approver',
    sourceLinks: [
      { sourceId: 'out_graphic', isAccountHolder: false },
      { sourceId: 'out_citi', isAccountHolder: false },
    ],
    active: true,
    avatarColor: 'bg-violet-500',
    joinedAt: '2025-08-15T00:00:00Z',
    notes: 'Local press desk contact',
  },
  {
    id: 'col_yaw',
    name: 'Yaw Darko',
    email: 'yaw@fairful.com',
    role: 'designer',
    sourceLinks: [],
    active: false,
    avatarColor: 'bg-rose-500',
    joinedAt: '2025-06-02T00:00:00Z',
  },
  {
    id: 'col_ama_creator',
    name: 'Ama Travels (manager)',
    email: 'ama.creator@example.com',
    phone: '+233 20 555 0199',
    role: 'social_manager',
    sourceLinks: [{ sourceId: 'acc_creator_ama', isAccountHolder: false }],
    active: true,
    avatarColor: 'bg-orange-500',
    joinedAt: '2026-03-01T00:00:00Z',
    notes: 'Talent manager — Ama holds the Instagram account herself',
  },
  {
    id: 'col_kwame_holder',
    name: 'Kwame Mensah',
    email: 'kwesi.creator@example.com',
    role: 'social_manager',
    sourceLinks: [{ sourceId: 'acc_kwame_tt', isAccountHolder: true }],
    active: true,
    avatarColor: 'bg-teal-500',
    joinedAt: '2026-04-12T00:00:00Z',
    notes: 'Creator and account holder for @kwame.eats',
  },
  {
    id: 'col_foa_ed',
    name: 'FOA Editorial desk',
    email: 'editorial@foundersofafrica.com',
    role: 'reviewer',
    sourceLinks: [{ sourceId: 'out_foa', isAccountHolder: false }],
    active: true,
    avatarColor: 'bg-indigo-500',
    joinedAt: '2026-02-18T00:00:00Z',
    notes: 'Partner media contact — not an account holder',
  },
];


function coerceIdPart(value: unknown, label: string): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  // SourceRef shape from the WIP sources.ts migration ({ sourceKind, sourceId }).
  if (value && typeof value === 'object' && 'sourceId' in value) {
    const ref = value as { sourceKind?: unknown; sourceId?: unknown };
    const id = coerceIdPart(ref.sourceId, label);
    if (ref.sourceKind === 'outlet') return id.startsWith('out_') ? id : `out_${id}`;
    if (ref.sourceKind === 'account') return id.startsWith('acc_') ? id : `acc_${id}`;
    return id;
  }
  throw new TypeError(`fixtures: ${label} must be a string/number/SourceRef, got ${typeof value}`);
}

function variantId(contentId: unknown, sourceId: unknown, format: unknown) {
  const c = coerceIdPart(contentId, 'contentId').replace(/^cnt_/, '');
  const s = coerceIdPart(sourceId, 'sourceId').replace(/^(acc_|out_)/, '');
  const f = coerceIdPart(format, 'format');
  return `var_${c}_${s}_${f}`;
}

function buildVariants(
  contentId: string,
  seed: string,
  specs: { sourceId: string; format: ContentFormat; body?: string; threadParts?: string[]; firstComment?: string }[],
): ContentVariant[] {
  return specs.map((spec) => {
    const sourceId = coerceIdPart(spec.sourceId, 'sourceId');
    return {
      id: variantId(contentId, sourceId, spec.format),
      sourceId,
      format: spec.format,
      body: spec.body ?? seed,
      threadParts: spec.threadParts,
      firstComment: spec.firstComment,
    };
  });
}

export const MARKETING_CONTENT: MarketingContentItem[] = [
  {
    id: 'cnt_summer_launch',
    title: 'Summer Stays 2026: expanding across West Africa',
    contentType: 'press_release',
    status: 'published',
    playId: 'play_h1_homes',
    body: '<p>Fairful today announced the expansion of its Summer Stays program...</p>',
    excerpt: 'Fairful expands its host-guaranteed marketplace to three new regions this summer.',
    authorIds: ['col_ama'],
    collaboratorIds: ['col_kwesi', 'col_naa'],
    channels: ['traditional', 'social'],
    sourceIds: ['acc_ff_ig', 'acc_ff_x', 'acc_ff_li', 'out_techcrunch', 'out_graphic'],
    variants: buildVariants('cnt_summer_launch', 'Summer stays are live across West Africa.', [
      { sourceId: 'acc_ff_ig', format: 'carousel', body: 'Summer stays are live ☀️ Swipe for Labadi · Cape Coast · Kumasi.', firstComment: '#FairfulSummer' },
      { sourceId: 'acc_ff_x', format: 'x_thread', body: 'Summer Stays are live across West Africa. Book with host guarantees → fairful.app/summer', threadParts: ['Every booking is backed by our host guarantee.', 'Available today in Labadi, Cape Coast, and Kumasi.'] },
      { sourceId: 'acc_ff_li', format: 'linkedin_post', body: 'We just expanded Summer Stays across West Africa — with host guarantees on every booking.' },
      { sourceId: 'out_techcrunch', format: 'pitch', body: 'Exclusive: Fairful expands Summer Stays with host-backed guarantees across West Africa.' },
      { sourceId: 'out_graphic', format: 'press_release', body: 'Fairful today announced the expansion of its Summer Stays program…' },
    ]),
    tags: ['launch', 'expansion'],
    featuredMedia: { kind: 'image', tone: 'bg-gradient-to-br from-sky-400 to-blue-700', label: 'Launch' },
    scheduledAt: '2026-07-22T08:00:00Z',
    publishedAt: '2026-07-22T08:00:00Z',
    createdAt: '2026-07-10T09:00:00Z',
    updatedAt: '2026-07-22T08:00:00Z',
  },
  {
    id: 'cnt_host_guide',
    title: 'A host’s guide to the new guarantee program',
    contentType: 'blog_post',
    status: 'in_review',
    playId: 'play_trust',
    body: '<p>Everything hosts need to know about the guarantee program...</p>',
    excerpt: 'Walking hosts through the new guarantee program, step by step.',
    authorIds: ['col_kwesi'],
    collaboratorIds: ['col_ama', 'col_jordan'],
    channels: ['social'],
    sourceIds: ['acc_ff_ig', 'acc_ff_tt', 'acc_creator_ama'],
    variants: buildVariants('cnt_host_guide', 'Everything hosts need to know about the guarantee program.', [
      { sourceId: 'acc_ff_ig', format: 'carousel', body: 'A host’s guide to the new guarantee — swipe for the 5 steps.' },
      { sourceId: 'acc_ff_tt', format: 'short', body: 'POV: your booking is guaranteed 👀 #FairfulSummer' },
      { sourceId: 'acc_creator_ama', format: 'reel', body: 'Partner reel brief: walk through guarantee sign-up in 30s.' },
    ]),
    tags: ['hosts', 'education'],
    createdAt: '2026-07-18T10:00:00Z',
    updatedAt: '2026-07-24T14:30:00Z',
  },
  {
    id: 'cnt_newsletter_aug',
    title: 'August newsletter: what’s new this month',
    contentType: 'newsletter',
    status: 'scheduled',
    playId: 'play_h1_homes',
    body: '<p>This month’s roundup of new stays, host stories, and travel tips...</p>',
    excerpt: 'Monthly roundup for subscribers.',
    authorIds: ['col_ama'],
    collaboratorIds: ['col_efua'],
    channels: ['social'],
    sourceIds: ['acc_ff_ig', 'acc_ff_fb', 'acc_ff_x'],
    variants: buildVariants('cnt_newsletter_aug', 'This month’s roundup of new stays, host stories, and travel tips.', [
      { sourceId: 'acc_ff_ig', format: 'story', body: 'August roundup is live — tap for the full newsletter.' },
      { sourceId: 'acc_ff_fb', format: 'social_post', body: 'What’s new this month for hosts and guests — August newsletter.' },
      { sourceId: 'acc_ff_x', format: 'x_post', body: 'August newsletter is out: new stays, host stories, travel tips → fairful.app/news' },
    ]),
    tags: ['newsletter'],
    scheduledAt: '2026-08-20T09:00:00Z',
    createdAt: '2026-07-20T11:00:00Z',
    updatedAt: '2026-08-10T09:00:00Z',
  },
  {
    id: 'cnt_video_script',
    title: 'Explainer video: how the host guarantee works',
    contentType: 'video_script',
    status: 'draft',
    playId: 'play_trust',
    body: '<p>Scene 1: open on a host receiving a booking notification...</p>',
    excerpt: '60-second explainer for the guarantee program.',
    authorIds: ['col_kwesi'],
    collaboratorIds: ['col_yaw'],
    channels: ['social'],
    sourceIds: ['acc_ff_ig'],
    variants: buildVariants('cnt_video_script', '60-second explainer for the guarantee program.', [
      { sourceId: 'acc_ff_ig', format: 'reel', body: 'Scene 1: host gets a booking notification…' },
    ]),
    tags: ['video', 'education'],
    createdAt: '2026-07-23T12:00:00Z',
    updatedAt: '2026-07-23T12:00:00Z',
  },
  {
    id: 'cnt_landing_summer',
    title: 'Summer Stays landing page copy',
    contentType: 'landing_page',
    status: 'approved',
    playId: 'play_h1_homes',
    body: '<p>Hero: Book your next escape, guaranteed...</p>',
    excerpt: 'Landing page copy for the Summer Stays campaign.',
    authorIds: ['col_ama'],
    collaboratorIds: ['col_naa', 'col_jordan'],
    channels: ['social'],
    sourceIds: ['acc_ff_ig'],
    variants: buildVariants('cnt_landing_summer', 'Hero: Book your next escape, guaranteed…', [
      { sourceId: 'acc_ff_ig', format: 'story', body: 'Summer stays — book your next escape, guaranteed. Link in bio.' },
    ]),
    tags: ['campaign', 'landing-page'],
    featuredMedia: { kind: 'image', tone: 'bg-gradient-to-br from-amber-300 via-rose-400 to-fuchsia-500', label: 'Summer' },
    createdAt: '2026-07-05T09:00:00Z',
    updatedAt: '2026-07-19T16:00:00Z',
  },
  {
    id: 'cnt_idea_podcast',
    title: 'Pitch: founder interview on marketplace trust',
    contentType: 'article',
    status: 'idea',
    playId: 'play_trust',
    body: '',
    excerpt: 'Idea for a founder interview placement.',
    authorIds: ['col_jordan'],
    collaboratorIds: [],
    channels: ['traditional'],
    sourceIds: ['out_techcrunch'],
    variants: buildVariants('cnt_idea_podcast', 'Idea for a founder interview placement.', [
      { sourceId: 'out_techcrunch', format: 'pitch', body: 'Pitch: founder interview on marketplace trust.' },
    ]),
    tags: ['pr', 'founder'],
    createdAt: '2026-07-24T08:00:00Z',
    updatedAt: '2026-07-24T08:00:00Z',
  },
];


/** Deterministic mock 7-day engagement trend ending near `finalRate` — no real time-series backend yet. */
function mockEngagementTrend(finalRate: number, days = 7): { date: string; engagementRate: number }[] {
  const base = new Date('2026-07-21T00:00:00Z').getTime();
  return Array.from({ length: days }, (_, i) => {
    const wobble = Math.sin(i * 1.7) * finalRate * 0.25;
    const ramp = finalRate * (0.55 + (i / (days - 1)) * 0.45);
    return {
      date: new Date(base - (days - 1 - i) * 86_400_000).toISOString().slice(0, 10),
      engagementRate: Math.max(0.001, ramp + wobble),
    };
  });
}

export const MARKETING_SOCIAL_POSTS: SocialPost[] = [
  {
    id: 'soc_ig_carousel',
    contentId: 'cnt_landing_summer',
    playId: 'play_h1_homes',
    variantId: 'var_landing_summer_ff_ig_story',
    sourceId: 'acc_ff_ig',
    format: 'carousel',
    platform: 'instagram',
    handle: '@fairful',
    caption: 'Summer stays are live ☀️ Swipe for Labadi · Cape Coast · Kumasi.',
    media: { kind: 'image', tone: 'bg-gradient-to-br from-amber-300 via-rose-400 to-fuchsia-500', label: 'Summer' },
    tags: ['launch', 'summer-campaign'],
    notes: [],
    firstComment: '#FairfulSummer #Accra #CapeCoast #Kumasi #TravelAfrica #HostGuarantee',
    status: 'published',
    scheduledAt: '2026-07-21T15:00:00Z',
    publishedAt: '2026-07-21T15:00:00Z',
    authorId: 'col_efua',
    approvedBy: 'col_naa',
    approvedAt: '2026-07-21T10:00:00Z',
    metrics: {
      impressions: 142_000,
      likes: 4200,
      shares: 880,
      comments: 310,
      engagementRate: 0.048,
      trend: mockEngagementTrend(0.048),
    },
  },
  {
    id: 'soc_x_launch',
    contentId: 'cnt_summer_launch',
    playId: 'play_h1_homes',
    variantId: 'var_summer_launch_ff_x_x_thread',
    sourceId: 'acc_ff_x',
    format: 'x_thread',
    platform: 'x',
    handle: '@fairful',
    caption: 'Summer Stays are live across West Africa. Book with host guarantees → fairful.app/summer',
    threadParts: [
      'Every booking is backed by our new host guarantee — verified stays, protected payments, real support if anything goes wrong.',
      'Available today in Labadi, Cape Coast, and Kumasi — more cities landing throughout the summer.',
    ],
    media: { kind: 'image', tone: 'bg-gradient-to-br from-sky-400 to-blue-700', label: 'Launch' },
    tags: ['launch', 'summer-campaign'],
    notes: [
      {
        id: 'note_x_launch_1',
        authorId: 'col_naa',
        body: 'Great open line — keep the thread short, 2 more tweets max.',
        createdAt: '2026-07-20T15:00:00Z',
      },
    ],
    status: 'published',
    scheduledAt: '2026-07-21T08:00:00Z',
    publishedAt: '2026-07-21T08:00:00Z',
    authorId: 'col_efua',
    approvedBy: 'col_naa',
    approvedAt: '2026-07-21T07:00:00Z',
    metrics: {
      impressions: 198_000,
      likes: 2100,
      shares: 640,
      comments: 180,
      engagementRate: 0.029,
      trend: mockEngagementTrend(0.029),
    },
  },
  {
    id: 'soc_li_guarantee',
    contentId: 'cnt_host_guide',
    playId: 'play_trust',
    platform: 'linkedin',
    handle: 'fairful',
    caption: 'Hosts deserve certainty. Our new guarantee covers verified bookings.',
    tags: ['hosts', 'trust'],
    notes: [],
    status: 'scheduled',
    scheduledAt: '2026-08-22T09:00:00Z',
    authorId: 'col_efua',
    approvedBy: 'col_naa',
    approvedAt: '2026-07-25T12:00:00Z',
  },
  {
    id: 'soc_tt_teaser',
    contentId: 'cnt_video_script',
    playId: 'play_trust',
    platform: 'tiktok',
    media: { kind: 'video', tone: 'bg-gradient-to-br from-fuchsia-600 via-rose-500 to-cyan-400', label: '0:18' },
    handle: '@fairful',
    caption: 'POV: your booking is guaranteed 👀 #FairfulSummer',
    tags: ['hosts', 'video'],
    notes: [
      {
        id: 'note_tt_1',
        authorId: 'col_kwesi',
        body: 'Waiting on the final video cut from design before this can go out — flagging for approval anyway so timing lines up.',
        createdAt: '2026-07-24T09:00:00Z',
      },
    ],
    status: 'pending_approval',
    authorId: 'col_efua',
  },
  {
    id: 'soc_fb_newsletter',
    contentId: 'cnt_newsletter_aug',
    playId: 'play_h1_homes',
    platform: 'facebook',
    handle: 'Fairful',
    caption: 'This month’s newsletter is out — new stays, host stories, and more.',
    tags: ['newsletter'],
    notes: [],
    status: 'draft',
    authorId: 'col_efua',
  },
];

export const MARKETING_POST_IDEAS: PostIdea[] = [
  {
    id: 'idea_host_spotlight',
    text: 'Weekly "host spotlight" series — short video interviews with top-rated hosts about what makes their stay special.',
    platform: 'tiktok',
    tags: ['hosts', 'series'],
    createdAt: '2026-07-22T10:00:00Z',
  },
  {
    id: 'idea_before_after',
    text: 'Before/after carousel of a host renovating their listing with Fairful design grants.',
    platform: 'instagram',
    tags: ['hosts', 'ugc'],
    createdAt: '2026-07-23T14:00:00Z',
  },
  {
    id: 'idea_guarantee_explainer',
    text: 'Quick-cut explainer: "3 things the host guarantee covers that you didn\'t know about."',
    tags: ['education', 'trust'],
    createdAt: '2026-07-24T11:00:00Z',
  },
];

export const MARKETING_POST_TEMPLATES: PostTemplate[] = [
  {
    id: 'tpl_launch_announcement',
    name: 'Launch announcement',
    body: '🎉 [WHAT] is live! [BENEFIT]. Available now → [LINK]',
  },
  {
    id: 'tpl_host_guarantee_x',
    name: 'Host guarantee (X)',
    body: 'Hosts deserve certainty. Our host guarantee covers [COVERAGE]. Learn more → fairful.app/guarantee',
    platform: 'x',
  },
  {
    id: 'tpl_ugc_reshare',
    name: 'UGC reshare',
    body: 'Found this gem from @[CREATOR] 🥹 [LISTING NAME] — save this for your next trip!',
    platform: 'instagram',
  },
];

export const MARKETING_ENGAGEMENT: EngagementComment[] = [
  {
    id: 'eng_ig_1',
    postId: 'soc_ig_carousel',
    platform: 'instagram',
    authorHandle: '@ama.travels',
    authorName: 'Ama Travels',
    body: 'This looks incredible 😍 does the guarantee cover cancellations too?',
    createdAt: '2026-07-21T16:10:00Z',
    sentiment: 'positive',
    status: 'unread',
  },
  {
    id: 'eng_x_1',
    postId: 'soc_x_launch',
    platform: 'x',
    authorHandle: '@accra_nights',
    authorName: 'Accra Nights',
    body: 'Following up from last week — has the payout issue actually been fixed or is this just marketing?',
    createdAt: '2026-07-21T09:20:00Z',
    sentiment: 'negative',
    status: 'unread',
  },
  {
    id: 'eng_x_2',
    postId: 'soc_x_launch',
    platform: 'x',
    authorHandle: '@dev_seeker',
    authorName: 'Kwame D.',
    body: 'Congrats on the launch! Is the team hiring in Accra?',
    createdAt: '2026-07-21T10:05:00Z',
    sentiment: 'neutral',
    status: 'replied',
    reply: 'Thank you! We are — check fairful.app/careers 🙌',
    repliedAt: '2026-07-21T11:00:00Z',
    repliedBy: 'col_efua',
  },
  {
    id: 'eng_tt_1',
    platform: 'tiktok',
    authorHandle: '@kwame.eats',
    authorName: 'Kwame Eats',
    body: 'When does the guarantee video drop?? Need this for my next trip 👀',
    createdAt: '2026-07-24T20:40:00Z',
    sentiment: 'positive',
    status: 'unread',
  },
  {
    id: 'eng_fb_1',
    platform: 'facebook',
    authorHandle: 'Accra Expats',
    authorName: 'Accra Expats Community',
    body: 'Anyone actually used the host guarantee yet? Curious how the claims process works.',
    createdAt: '2026-07-25T08:30:00Z',
    sentiment: 'neutral',
    status: 'archived',
  },
];

export const MARKETING_TRADITIONAL_PLACEMENTS: TraditionalPlacement[] = [
  {
    id: 'trd_techcrunch',
    contentId: 'cnt_summer_launch',
    playId: 'play_h1_homes',
    sourceId: 'out_techcrunch',
    outlet: 'TechCrunch',
    medium: 'online_news',
    status: 'published',
    scheduledAt: '2026-07-22T08:00:00Z',
    publishedAt: '2026-07-22T08:00:00Z',
    authorId: 'col_ama',
    url: 'https://techcrunch.com/example/fairful-summer',
  },
  {
    id: 'trd_graphic',
    contentId: 'cnt_summer_launch',
    playId: 'play_h1_homes',
    sourceId: 'out_graphic',
    outlet: 'Daily Graphic',
    medium: 'print',
    status: 'published',
    scheduledAt: '2026-07-20T06:00:00Z',
    publishedAt: '2026-07-20T06:00:00Z',
    authorId: 'col_ama',
  },
  {
    id: 'trd_citi',
    playId: 'play_h1_homes',
    outlet: 'Citi FM / Channel One',
    medium: 'radio',
    status: 'scheduled',
    scheduledAt: '2026-08-25T07:30:00Z',
    authorId: 'col_jordan',
  },
  {
    id: 'trd_podcast',
    contentId: 'cnt_idea_podcast',
    playId: 'play_trust',
    outlet: 'Founders of Africa',
    medium: 'podcast',
    status: 'scheduled',
    scheduledAt: '2026-09-03T14:00:00Z',
    authorId: 'col_jordan',
  },
];

export const MARKETING_WORK_FEEDBACK: WorkFeedback[] = [
  {
    id: 'fb_host_guide_tone',
    targetType: 'content',
    targetId: 'cnt_host_guide',
    authorId: 'col_ama',
    body: 'Can we soften the guarantee claims in the second section? Legal flagged “covers every booking” as too absolute.',
    taggedIds: ['col_kwesi', 'col_jordan'],
    status: 'open',
    createdAt: '2026-07-24T09:15:00Z',
  },
  {
    id: 'fb_host_guide_cta',
    targetType: 'content',
    targetId: 'cnt_host_guide',
    authorId: 'col_naa',
    body: 'Please check the CTA — should point to /hosts/guarantee once the landing page is approved.',
    taggedIds: ['col_ama'],
    status: 'open',
    createdAt: '2026-07-24T16:40:00Z',
  },
  {
    id: 'fb_x_thread',
    targetType: 'social_post',
    targetId: 'soc_x_launch',
    authorId: 'col_naa',
    body: 'Great open line — keep the thread short, 2 more tweets max.',
    taggedIds: ['col_efua'],
    status: 'resolved',
    createdAt: '2026-07-20T15:00:00Z',
    resolvedAt: '2026-07-20T17:20:00Z',
    resolvedBy: 'col_efua',
  },
  {
    id: 'fb_tt_hook',
    targetType: 'social_post',
    targetId: 'soc_tt_teaser',
    authorId: 'col_jordan',
    body: 'POV hook works — please check the on-screen text doesn’t collide with TikTok UI chrome.',
    taggedIds: ['col_yaw', 'col_efua'],
    status: 'open',
    createdAt: '2026-07-25T11:05:00Z',
  },
  {
    id: 'fb_tc_pitch',
    targetType: 'traditional_placement',
    targetId: 'trd_techcrunch',
    authorId: 'col_jordan',
    body: 'Pitch draft ready — Naa, can you check the local angle before we send?',
    taggedIds: ['col_naa'],
    status: 'open',
    createdAt: '2026-07-23T14:00:00Z',
  },
];

export const MARKETING_TRENDS: TrendMetric[] = [
  { id: 'trend_summer', label: '#FairfulSummer', channel: 'social', volume: 3400, changePct: 0.42, sentiment: 'positive' },
  { id: 'trend_guarantee', label: 'host guarantee', channel: 'both', volume: 1280, changePct: 0.18, sentiment: 'positive' },
  { id: 'trend_expansion', label: 'West Africa expansion', channel: 'traditional', volume: 640, changePct: 0.09, sentiment: 'neutral' },
  { id: 'trend_refunds', label: 'refund delays', channel: 'social', volume: 210, changePct: -0.12, sentiment: 'negative' },
  { id: 'trend_ugc', label: 'creator UGC', channel: 'social', volume: 1890, changePct: 0.65, sentiment: 'positive' },
];

const days = (n: number, base = new Date('2026-07-26T00:00:00Z').getTime()) =>
  Array.from({ length: n }, (_, i) => new Date(base - (n - 1 - i) * 86_400_000).toISOString().slice(0, 10));

export const KPI_HISTORY_DAYS = 30;

export const MARKETING_KPIS: KpiMetric[] = [
  {
    id: 'kpi_engagement_rate',
    label: 'Avg. engagement rate',
    value: 4.1,
    previousValue: 3.4,
    unit: 'percent',
    series: days(KPI_HISTORY_DAYS).map((date, i) => ({
      date,
      value: 3.2 + (i % 7) * 0.15 + (i % 3 === 0 ? 0.3 : 0),
    })),
  },
  {
    id: 'kpi_reach',
    label: 'Combined reach',
    value: 1_240_000,
    previousValue: 980_000,
    unit: 'number',
    series: days(KPI_HISTORY_DAYS).map((date, i) => ({
      date,
      value: 780_000 + (i % 10) * 34_000 + Math.floor(i / 10) * 20_000,
    })),
  },
  {
    id: 'kpi_follower_growth',
    label: 'Follower growth',
    value: 5.6,
    previousValue: 3.9,
    unit: 'percent',
    series: days(KPI_HISTORY_DAYS).map((date, i) => ({ date, value: 2.8 + (i % 10) * 0.25 })),
  },
  {
    id: 'kpi_ctr',
    label: 'Click-through rate',
    value: 2.3,
    previousValue: 2.6,
    unit: 'percent',
    series: days(KPI_HISTORY_DAYS).map((date, i) => ({ date, value: 2.9 - (i % 12) * 0.06 })),
  },
];

export const MARKETING_SENTIMENT: SentimentPoint[] = days(KPI_HISTORY_DAYS).map((date, i) => ({
  date,
  positive: 60 + (i % 5 === 0 ? 8 : 0) + (i % 7) * 1.5,
  neutral: 30 - (i % 7) * 0.8,
  negative: Math.max(2, 10 - (i % 7) * 0.6),
}));

export function formatCompact(n?: number): string {
  if (n == null) return '—';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return String(n);
}

/** Null value is a dash, never 0. Ratio tiles are fractions (0.042 → 4.20%). */
export function formatKpiValue(kpi: { unit: 'ratio' | 'number'; value: number | null }): string {
  if (kpi.value == null) return '—';
  if (kpi.unit === 'ratio') return `${(kpi.value * 100).toFixed(2)}%`;
  return formatCompact(kpi.value);
}
